import { Box, Button } from '@mui/material';
import _ from 'lodash';
import AddIcon from '@mui/icons-material/Add';
import { getFormFields } from 'utils/form-utils';
import { IPerformanceSummaryTableProps } from 'models/loan-projection/performance-summary';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Delete } from '@mui/icons-material';
import DataTable from 'components/common/data-table';
import SimpleFormLabel from 'components/common/form-components/form-label';
import { MULTI_LINE_INPUT } from 'constants/form-components';
import TooltipIconButton from 'components/common/tooltip-icon-button';
import getIcon from 'icons/icons';
import { ADD, DELETE } from 'icons/icon-names';
import { useTheme } from '@mui/styles';
import { useCallback } from 'react';
import { formatGridNumber } from 'utils/common-utils';
import { NumericEditor } from 'components/common/data-table/numericEditor';

interface IProps extends IPerformanceSummaryTableProps {}

export default function SourceAndUseOfFunds({ t, control, getValues, setValue, watchAllFields }: IProps) {
  const { state } = useLocation();
  const theme = useTheme();

  const useFundsColDefs = useMemo(() => {
    var colDef: any = [
      {
        headerName: 'Use of Funds',
        children: [
          {
            field: 'sl',
            headerName: 'S.No',
            editable: state.viewOnly ? false : true,
            width: 150,
          },
          {
            field: 'particular',
            headerName: 'Particulars',
            editable: state.viewOnly ? false : true,
            width: 150,
          },
          {
            field: 'amount',
            headerName: 'Amount',
            valueFormatter: formatGridNumber,
            cellEditor: NumericEditor,
            cellStyle: { textAlign: 'right' },
            editable: state.viewOnly ? false : true,
            width: 150,
          },
        ],
      },
    ];
    if (!state.viewOnly) {
      colDef.push({
        field: '',
        headerName: 'Actions',
        width: 100,
        pinned: 'right',
        suppressSizeToFit: true,
        cellRendererFramework: (params: any) => {
          return (
            <>
              {params.rowIndex === getValues('useOfFunds').length - 1 && (
                <TooltipIconButton
                  onClick={() => {
                    params.api.stopEditing();
                    setTimeout(() => {
                      let fields = [...getValues('useOfFunds'), {}];
                      setValue('useOfFunds', fields);
                      params.api.setRowData(fields);
                    }, 100);
                  }}
                  icon={getIcon(ADD, theme.palette.primary.main)}
                  title="Add"
                />
              )}
              {getValues('useOfFunds').length > 1 && (
                <TooltipIconButton
                  onClick={(e: any) => {
                    let updatedFields = [...getValues('useOfFunds')];
                    if (updatedFields.length > 0) {
                      updatedFields.splice(params.node.rowIndex, 1);
                      setValue('useOfFunds', updatedFields);
                      params.api.setRowData(updatedFields);
                    }
                  }}
                  icon={getIcon(DELETE, theme.palette.error.main)}
                  title="Delete"
                />
              )}
            </>
          );
        },
      });
    }
    return colDef;
  }, []);

  const sourceFundsColDefs = useMemo(() => {
    var colDef: any = [
      {
        headerName: 'Source of Funds',
        children: [
          {
            field: 'sl',
            headerName: 'S.No',
            editable: state.viewOnly ? false : true,
            width: 150,
          },
          {
            field: 'particular',
            headerName: 'Particulars',
            editable: state.viewOnly ? false : true,
            width: 150,
          },
          {
            field: 'amount',
            headerName: 'Amount',
            valueFormatter: formatGridNumber,
            cellEditor: NumericEditor,
            cellStyle: { textAlign: 'right' },
            editable: state.viewOnly ? false : true,
            width: 150,
          },
        ],
      },
    ];
    if (!state.viewOnly) {
      colDef.push({
        field: '',
        headerName: 'Actions',
        width: 100,
        pinned: 'right',
        suppressSizeToFit: true,
        cellRendererFramework: (params: any) => {
          return (
            <>
              {params.rowIndex === getValues('sourceFunds').length - 1 && (
                <TooltipIconButton
                  onClick={() => {
                    params.api.stopEditing();
                    setTimeout(() => {
                      let fields = [...getValues('sourceFunds'), {}];
                      setValue('sourceFunds', fields);
                      params.api.setRowData(fields);
                    }, 100);
                  }}
                  icon={getIcon(ADD, theme.palette.primary.main)}
                  title="Add"
                />
              )}
              {getValues('sourceFunds').length > 1 && (
                <TooltipIconButton
                  onClick={(e: any) => {
                    let updatedFields = [...getValues('sourceFunds')];
                    if (updatedFields.length > 0) {
                      updatedFields.splice(params.node.rowIndex, 1);
                      setValue('sourceFunds', updatedFields);
                      params.api.setRowData(updatedFields);
                    }
                  }}
                  icon={getIcon(DELETE, theme.palette.error.main)}
                  title="Delete"
                />
              )}
            </>
          );
        },
      });
    }
    return colDef;
  }, []);

  const onNonFundCellValueChanged = useCallback((event) => {
    let updatedRowData: any = [];
    event.api.forEachNode((e: any) => {
      updatedRowData.push(e.data);
    });
    setValue('useOfFunds', updatedRowData);
  }, []);

  const onSourceFundsCellValueChanged = useCallback((event) => {
    let updatedRowData: any = [];
    event.api.forEachNode((e: any) => {
      updatedRowData.push(e.data);
    });
    setValue('sourceFunds', updatedRowData);
  }, []);

  return (
    <>
      <Box sx={{ pl: 0.5, pr: 0.5, mb: 7 }}>
        <Box sx={{ mt: 1 }}>
          <DataTable
            columnDefs={useFundsColDefs}
            autoSize={true}
            rowData={watchAllFields?.useOfFunds}
            pagination={false}
            domLayout={'autoHeight'}
            sizeToFit={true}
            style={{ height: '100%' }}
            onCellValueChanged={onNonFundCellValueChanged}
          />
        </Box>
      </Box>
      <Box sx={{ pl: 0.5, pr: 0.5, mb: 7 }}>
        <Box sx={{ mt: 1 }}>
          <DataTable
            columnDefs={sourceFundsColDefs}
            autoSize={true}
            rowData={watchAllFields?.sourceFunds}
            pagination={false}
            domLayout={'autoHeight'}
            sizeToFit={true}
            style={{ height: '100%' }}
            onCellValueChanged={onSourceFundsCellValueChanged}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', mt: 2 }}>
        <SimpleFormLabel label={'Comment'} sx={{ width: '25%', placeSelf: 'flex-start' }} />
        <Box sx={{ width: '75%' }}>
          {getFormFields(
            {
              name: 'assessmentLimits.termLoans.comments',
              component: MULTI_LINE_INPUT,
              label: 'Comment',
            },
            control,
            state.viewOnly
          )}
        </Box>
      </Box>
    </>
  );
}
